import { initializeApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCustomToken,
  signOut as fbSignOut,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// Local dev: route Firebase Auth to the emulator that prospin-api runs
// against. Without this, the admin signs in against real Firebase (token
// aud=prospin-38fcb) but the local API verifies against the emulator project,
// so every authenticated call is rejected on an aud mismatch.
// VITE_FIREBASE_PROJECT_ID must match the emulator/backend project
// (prospin-local). Double-guarded with import.meta.env.DEV so a stray env var
// can never make a production build talk to an emulator.
const emulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
if (import.meta.env.DEV && emulatorHost) {
  connectAuthEmulator(auth, `http://${emulatorHost}`, { disableWarnings: true });
}

const googleProvider = new GoogleAuthProvider();

export async function signInWithGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

// Sign in from a Firebase custom token (minted by prospin-api's phone-auth).
// Admin sign-in is Google-popup only, which can't be driven headlessly, so the
// browser-smoke gate mints a custom token via the API's LocalTestProvider and
// injects it through the DEV-only window hook below. Double-guarded with
// import.meta.env.DEV so this affordance can never exist in a production build
// (Vite strips the dead branch), exactly like the emulator wiring above.
export async function signInWithCustomTokenString(token: string): Promise<User> {
  const result = await signInWithCustomToken(auth, token);
  return result.user;
}

if (import.meta.env.DEV) {
  (window as unknown as { __signInWithCustomToken?: (t: string) => Promise<User> })
    .__signInWithCustomToken = signInWithCustomTokenString;
}

export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

/**
 * Returns the current Firebase ID token, refreshing if forceRefresh is
 * true or if the cached token is near expiry (the SDK handles the latter).
 */
export async function getIdToken(forceRefresh = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export { onAuthStateChanged, type User };
